"use client";

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useRef } from "react";
import { I18n } from "@local/offer/src/lib/i18n";
import { clearDecimals } from "@local/utils/src/clearDecimals";
import { convertToUsd } from "../convertToUsd";
import { trackAction } from "../trackActions";
interface PriceViewBeaconProps {
  /**
   * The rendered price element to watch. `price_view` fires the first time
   * this node scrolls into the viewport: only once the user has
   * reached the price, not on initial page load (unless the price
   * is already on screen).
   */
  targetRef: RefObject<HTMLElement | null>;
  market: string;
  /** Final displayed price, in cents (matches Offer price values). */
  priceInCents: number;
  channel?: string;
  ctaId?: string;
}

/**
 * Fires a `price_view` event the first time the final price enters the
 * viewport, so price-exposure rate can be correlated with drop-off.
 *
 * Observes the real price node via IntersectionObserver (threshold 0.5 → at
 * least half the price visible), fires exactly once, then disconnects. Renders
 * nothing itself, so it adds no DOM and no layout cost.
 */
export const PriceViewBeacon = t0 => {
  const $ = _c(7);
  const {
    targetRef,
    market,
    priceInCents,
    channel,
    ctaId
  } = t0;
  const fired = useRef(false);
  let t1;
  let t2;
  if ($[0] !== channel || $[1] !== ctaId || $[2] !== market || $[3] !== priceInCents || $[4] !== targetRef) {
    t1 = () => {
      const element = targetRef.current;
      if (!element || fired.current) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || fired.current) {
            continue;
          }
          fired.current = true;
          observer.disconnect();
          const currency = I18n.getCurrencyCodeFromMarket(market);
          trackAction("price_view", {
            value: clearDecimals(priceInCents / 100, 2),
            currency,
            price_usd: convertToUsd(priceInCents, currency),
            ...(channel && {
              channel
            }),
            ...(ctaId && {
              cta_id: ctaId
            })
          }, {
            ga: false,
            datalake: true,
            posthog: true
          });
        }
      }, {
        threshold: 0.5
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    t2 = [targetRef, market, priceInCents, channel, ctaId];
    $[0] = channel;
    $[1] = ctaId;
    $[2] = market;
    $[3] = priceInCents;
    $[4] = targetRef;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  return null;
};